import axios from "axios";
import { IApplicationAgent } from "../models/application.model";
import { IBroker } from "../models/broker.model";
import { FULFILLED, PENDING } from "./action-type.util";

export const transformAgent = (agent: IBroker, makePrimary: boolean = false): IApplicationAgent => {
  return {
    ...agent,
    id: agent.brokerID,
    agentId: `${agent.brokerID}`,
    agentPercentage: makePrimary ? 100 : 0,
    agentFullName: agent.brokerName,
    agentCompanyName: agent.companyName,
    agentBranchName: agent.branchName,
    selectAgentAffiliates: agent.brokerAffiliates,

    affiliateId: agent.brokerAffiliates
      ? agent.brokerAffiliates.find(x => x.affiliateName === agent.affiliateName)?.affiliateId
      : null,

    applicationAgentId: 0,
    agentHierarchyName: "",
    agentHierarchyParent: "",
    agentHierarchyTier: "",
    primary: makePrimary,
    agentCommissionLevel: 2,
    applicationId: 0,
  };
};

export const APPLICATION_AGENT_ACTION_TYPES = {
  FETCH_APP_AGENTS: "applications/FETCH_APP_AGENTS",
  FETCH_BROKERS: "applicationBuilder/FETCH_BROKERS",
  BROKER_PICKER_PICK: "applicationBuilder/BROKER_PICKER_PICK",
  BROKER_PICKER_SELECTED: "applicationBuilder/BROKER_PICKER_SELECTED",
  BROKER_PICKER_DISMISS: "applicationBuilder/BROKER_PICKER_DISMISS",
  REMOVE_AGENT: "applicationBuilder/REMOVE_AGENT",
};

const initialState = {
  agents: [],
  agentsLoaded: false,
  agentsPickerList: [],
  agentsPickerSelected: null,
  agentsNotFound: false,
};

export type ApplicationAgentsState = Readonly<typeof initialState>;

/**
 * ApplicationMemos Reducer
 */

export default (state: ApplicationAgentsState = initialState, action): ApplicationAgentsState => {
  switch (action.type) {
    case PENDING(APPLICATION_AGENT_ACTION_TYPES.FETCH_APP_AGENTS):
      return {
        ...state,
        agents: [],
        agentsLoaded: false,
      };

    case FULFILLED(APPLICATION_AGENT_ACTION_TYPES.FETCH_APP_AGENTS):
      return {
        ...state,
        agents: [...action.payload.data],
        agentsLoaded: true,
      };

    case PENDING(APPLICATION_AGENT_ACTION_TYPES.FETCH_BROKERS):
      return {
        ...state,
        agentsPickerList: [],
        agentsNotFound: false,
      };

    case FULFILLED(APPLICATION_AGENT_ACTION_TYPES.FETCH_BROKERS):
      const items = action.payload.data.items;
      return {
        ...state,
        agentsPickerSelected: items.length === 1 ? transformAgent(items[0]) : null,
        agentsPickerList: items.length > 1 ? items : [],
        agentsNotFound: items.length === 0,
      };

    case APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_PICK:
      return {
        ...state,
        agents: [...state.agents, transformAgent(action.payload)],
        agentsPickerList: [],
      };

    case APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_SELECTED:
      return {
        ...state,
        agentsPickerSelected: action.payload,
      };

    case APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_DISMISS:
      return {
        ...state,
        agentsPickerList: [],
        agentsNotFound: false,
      };

    case APPLICATION_AGENT_ACTION_TYPES.REMOVE_AGENT:
      return {
        ...state,
        agents: state.agents.map(agent =>
          agent.agentId === action.payload.agentId ? { ...agent, agentRemovedByUser: true } : agent,
        ),
      };

    default:
      return state;
  }
};

/**
 * Fetch application agents for a given application
 */
export const fetchApplicationAgents = (appId: string) => ({
  type: APPLICATION_AGENT_ACTION_TYPES.FETCH_APP_AGENTS,
  payload: axios.get(`applications/${appId}/agents`),
});

/**
 * Search for brokers using a search text
 */
export const searchBrokerByText = (text: string) => ({
  type: APPLICATION_AGENT_ACTION_TYPES.FETCH_BROKERS,
  payload: axios.get(`brokers?searchText=${encodeURIComponent(text)}`),
});

/**
 * Pick a broker from the search result (adds them as agent)
 */
export const brokerPickerPickBroker = (broker: IApplicationAgent) => ({
  type: APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_PICK,
  payload: broker,
});

/**
 * Select a broker
 */
export const agentPickerSelect = (broker: IApplicationAgent | null) => ({
  type: APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_SELECTED,
  payload: broker,
});

/**
 * Dismiss the picker dropdown
 */
export const agentPickerDismiss = () => ({
  type: APPLICATION_AGENT_ACTION_TYPES.BROKER_PICKER_DISMISS,
});

/**
 * Mark agent as removed by user
 */
export const appBuilderRemoveBroker = (agent: IApplicationAgent) => ({
  type: APPLICATION_AGENT_ACTION_TYPES.REMOVE_AGENT,
  payload: agent,
});

/**
 * Save agents for a given application (including removed ones)
 */
export const saveAgents = (
  appId: number,
  primaryID: string,
  agents: IApplicationAgent[],
  removedAgents: IApplicationAgent[],
) => {
  const data = [...agents, ...removedAgents].map(agent => ({
    ...agent,
    agentCommissionLevel: agent.agentId === primaryID ? 1 : 2,
    primary: agent.agentId === primaryID,
  }));

  return {
    type: "applicationBuilder/SAVE_AGENTS",
    payload: axios.post(`applications/${appId}/agents`, data, {
      headers: { "Content-Type": "application/json" },
    }),
  };
};
